import { Dispatch, SetStateAction, useCallback, useState } from 'react'
import { useRouter } from 'next/router'
import uniqBy from 'lodash/uniqBy'

import { getIsUnderMov, getSupplierOrderData, getUnderMovProductList } from '../../lib/cart'
import useSharedCart from './useSharedCart'
import { useCart } from './CartProvider'
import { getCountryAndLocaleStrings } from '../../utils/locales'
import useUrls from '../../services/useUrls'
import useEcomEvents from '../../services/analytics/useEcomEvents'
import { Product } from '../../utils/types/Product'
import { CartProduct } from '../../utils/types/Order'
import { isCartProduct, isProductInclude } from '../../utils/types/guards/Order'
import { getProductName } from '../../lib/text'
import { useAuth } from '../../services/useAuth'

const useBeginCheckout = (
  setIsExistingCartModalOpen: Dispatch<SetStateAction<boolean>>,
  addSharedCartProductsToCart: () => void,
) => {
  const [isUnavailableProductsModalOpen, setUnavailableProductsModalOpen] = useState(false)
  const [lowStockProducts, setLowStockProducts] = useState<(Product | CartProduct)[]>([])

  const { query, locale: countryAndLocale } = useRouter()
  const { pushT } = useUrls()
  const { beginCheckoutEvent, triggerCheckoutAbandonmentFlowEvent } = useEcomEvents()
  const { cart: sharedCart, modifiedSharedCart, existingCartProductsCount } = useSharedCart()
  const {
    cartState: { cart: mainCart },
    modifiedCart: modifiedMainCart,
    deleteProduct,
    updateProduct,
    toggleMovModal,
  } = useCart()
  const { user, isVatIncluded } = useAuth()

  const { shareParams } = query
  const { country } = getCountryAndLocaleStrings(countryAndLocale)
  const cart = shareParams ? sharedCart : mainCart
  const modifiedCart = shareParams ? modifiedSharedCart : modifiedMainCart
  const supplierOrderData = modifiedCart?.supplierMappedProducts && cart
    ? getSupplierOrderData(
      modifiedCart?.supplierMappedProducts,
      cart,
      country,
      { shouldIncludeVat: isVatIncluded },
    ) : []

  const hasLowerMovBrand = supplierOrderData.some(({ total, mov }) => (
    getIsUnderMov(total, mov)
  ))

  const getVariant = (product: Product, variantId: string) => product?.product
    ?.find((variant: Product) => variant.id === variantId.toString())

  const deleteInvalidProducts = async () => {
    if (!modifiedCart || !cart) {
      // Can't delete if cart is not loaded yet
      return
    }
    const { underMoqProductList, unavailableProductList } = modifiedCart
    const lowStockIds = lowStockProducts
      .map((prod) => (!isCartProduct(prod) ? prod.id : undefined))
      .filter((prod) => prod !== undefined)
    const underMovProductList = getUnderMovProductList(cart, country, isVatIncluded)

    const underMoqUrls = underMoqProductList.map((product) => product.links.self.href)
    const unavailableUrls = unavailableProductList.map((product) => product.links.self.href)
    const underMovUrls = underMovProductList.map((product) => product.links.self.href)

    const lowStockUrlWithQuantity = modifiedCart.included
      .filter(isProductInclude)
      .filter((prod) => lowStockIds.includes(prod.attributes['order.product.productid']))
      .map((prod) => ({
        url: prod.links.self.href,
        quantity: prod.product
          ? getVariant(prod.product, prod.attributes['order.product.productid'])?.['stock.stocklevel']
          : 0,
      }))
      .reverse()

    const outOfStockUrls = lowStockUrlWithQuantity
      .filter((item) => item.quantity === 0)
      .map((item) => item.url)

    const updateLowStockProducts = lowStockUrlWithQuantity
      .filter((item) => item.quantity !== 0)

    const deleteUrls = [
      ...underMoqUrls,
      ...unavailableUrls,
      ...underMovUrls,
      ...outOfStockUrls,
    ].filter((item) => Boolean(item))

    if (deleteUrls.length) {
      await deleteProduct(deleteUrls)
    }

    if (updateLowStockProducts.length) {
      updateLowStockProducts.forEach(async (prod) => {
        await updateProduct(prod.url, {
          quantity: prod.quantity?.toString() || '0',
        })
      })
    }
  }

  const checkUnavailableProducts = () => {
    const unavailableProducts: (Product | CartProduct)[] = []

    if (!modifiedCart || !cart) {
      // Can't delete if cart is not loaded yet
      return false
    }
    const { unavailableProductList } = modifiedCart

    if (unavailableProductList?.length) {
      unavailableProducts.push(...uniqBy(unavailableProductList, (prod) => prod.attributes['order.product.prodcode']).map((product) => product.attributes))
    }

    cart?.included
      ?.filter(isProductInclude)
      ?.filter((product) => Boolean(product.product))
      ?.forEach((basketProduct) => {
        const exactStockLevel = basketProduct.product?.['stock.stocklevel']
        const basketProductId = basketProduct.attributes['order.product.productid']

        if (basketProductId === basketProduct.product?.['product.id']) {
          if (exactStockLevel !== null && basketProduct.attributes['order.product.quantity'] > Number(exactStockLevel)) {
            unavailableProducts.push(basketProduct.product)
          }
        }

        // Variant product case
        const outOfStockVariant = basketProduct.product?.product?.find(
          (variant) => (variant['product.id'] === basketProduct.attributes['order.product.productid']
              || variant.attribute.find((attr) => attr['product.lists.type'] === 'variant')?.['attribute.id'] === basketProduct.attributes.attribute?.[0]['order.product.attribute.id']
          )
            && variant['stock.stocklevel'] !== null
            && basketProduct.attributes['order.product.quantity'] > Number(variant['stock.stocklevel']),
        )
        if (outOfStockVariant) {
          unavailableProducts.push({ ...outOfStockVariant, 'text.name': getProductName(basketProduct.product), media: basketProduct.product?.media || [] })
        }
      })

    if (unavailableProducts.length) {
      setLowStockProducts(unavailableProducts)
      return true
    }

    return false
  }

  const handleBeginCheckout = useCallback(async () => {
    toggleMovModal(false)

    if (!isUnavailableProductsModalOpen && checkUnavailableProducts()) {
      setUnavailableProductsModalOpen(true)

      return
    }

    // If not on shared cart, move to checkout and exit function
    if (!shareParams) {
      await deleteInvalidProducts()
      await pushT('/checkout/address')

      // Analytics events
      beginCheckoutEvent()
      if (user?.email) {
        triggerCheckoutAbandonmentFlowEvent(user.email, false)
      }

      return
    }

    if (existingCartProductsCount > 0) {
      setIsExistingCartModalOpen(true)
    } else {
      await deleteInvalidProducts()
      addSharedCartProductsToCart()
    }
  }, [existingCartProductsCount, cart, isUnavailableProductsModalOpen])

  return {
    handleCheckoutButtonClick: handleBeginCheckout,
    hasLowerMovBrand,
    lowStockProducts,
    isUnavailableProductsModalOpen,
    setUnavailableProductsModalOpen,
  }
}

export default useBeginCheckout
